import type { TFunction } from "i18next";
import type { Journey, JourneyStep, JourneyTask } from "../../types/journey";

export interface AskEvePrefillInput {
	task: JourneyTask;
	journey: Journey;
	step: JourneyStep | null;
	t: TFunction;
}

/**
 * Builds the pre-filled chat question string for "Ask Eve" on a journey task.
 *
 * - If `step` is null, uses `chat.askEvePrefillNoStep` (no step context).
 * - If `step` is present but `task.subStepTitle` is absent, uses
 *   `chat.askEvePrefillNoSubStep` ("…under step {{step}}").
 * - If `step` and `task.subStepTitle` are both present, uses
 *   `chat.askEvePrefill` ("…under step {{step}}: {{subStep}}").
 *
 * The journey display name is resolved from `journey.treatmentType`.
 */
export function buildAskEvePrefill({
	task,
	journey,
	step,
	t,
}: AskEvePrefillInput): string {
	const journeyName = journey.treatmentType;
	const stepTitle = step?.title ?? null;
	const subStepTitle = task.subStepTitle || null;

	if (!stepTitle) {
		return t("chat:askEvePrefillNoStep", {
			task: task.title,
			journey: journeyName,
		});
	}

	if (subStepTitle) {
		return t("chat:askEvePrefill", {
			task: task.title,
			journey: journeyName,
			step: stepTitle,
			subStep: subStepTitle,
		});
	}

	return t("chat:askEvePrefillNoSubStep", {
		task: task.title,
		journey: journeyName,
		step: stepTitle,
	});
}
